"use client";

import Link from "next/link";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { Swiper as SwiperInterface } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";

import CaretDownIcon from "public/next-public/common/icons/caret-solid-down.svg";
import ChevronLeftIcon from "public/next-public/common/icons/caret-regular-left.svg";
import ChevronRightIcon from "public/next-public/common/icons/caret-regular-right.svg";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setVariantAttributeExpanded } from "@/store/variantSelectorSlice";

import ProductVariantAttribute from "./productVariantAttribute";
import style from "./productVariantAttributeValuesSwiper.module.scss";

// Leading back-arrow overlay width: 32px button + 16px gradient fade.
const SELECTED_TILE_LEFT_OFFSET = 48;

// How many tiles each scroll-arrow click advances the row.
const TILES_PER_SCROLL = 2;

function ProductVariantAttributeValuesSwiper({
  atributeName,
  atributeValue,
  productAttributeState,
  onVariantNavigationStart,
  variantNavigationLocked = false,
}: ProductVariantAttributeValuesSwiperProps) {
  const [swiperInstance, setSwiperInstance] = useState<SwiperInterface>();
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  // Persisted in Redux (keyed by attribute name) so it survives navigating between variants,
  // which remounts this component and would otherwise reset a local useState.
  const dispatch = useAppDispatch();
  const expanded = useAppSelector((state) => state.variantSelectorSlice.expandedByAttribute[atributeName] ?? false);

  const syncNavigationArrowsFromSwiper = useCallback((): void => {
    if (swiperInstance === undefined || swiperInstance.destroyed) {
      return;
    }
    // Show each arrow only when there is more to scroll in that direction: no "prev" at the
    // far left, no "next" at the far right (fully hidden, not greyed). When everything fits,
    // both isBeginning and isEnd are true, so neither arrow - nor the "Show All" toggle - shows.
    setShowPrev(swiperInstance.isBeginning === false);
    setShowNext(swiperInstance.isEnd === false);
    setHasOverflow(!(swiperInstance.isBeginning && swiperInstance.isEnd));
  }, [swiperInstance]);

  // Advance a couple tiles per click with the default animated transition. slideTo() is
  // index-based (and clamps at the ends), so it works from the off-snap position the selected
  // tile is nudged to - unlike slidePrev()/slideNext(), which look up the current translate in
  // the snap grid and jump to the start when it isn't a snap point.
  const scrollBySlide = useCallback(
    (direction: 1 | -1): void => {
      if (swiperInstance === undefined || swiperInstance.destroyed) {
        return;
      }
      swiperInstance.slideTo(swiperInstance.activeIndex + direction * TILES_PER_SCROLL);
    },
    [swiperInstance],
  );

  const selectedAttributeValueSlideIndex = useMemo((): number => {
    for (let i = 0; i < productAttributeState.productAttributeOptions.length; i++) {
      if (
        productAttributeState.productAttributeOptions[i].attributeValueId ===
        productAttributeState.selectedAttributeValueId
      ) {
        return i;
      }
    }
    return 0;
  }, [productAttributeState.productAttributeOptions, productAttributeState.selectedAttributeValueId]);

  const positionSelectedSlide = useCallback((): void => {
    if (swiperInstance === undefined || swiperInstance.destroyed) {
      return;
    }
    // Nudge the selected tile past the back-arrow overlay so the arrow never covers it.
    // translateTo clamps to the start, so the first option (no arrow) stays flush left.
    const selectedSlideLeft = swiperInstance.slidesGrid[selectedAttributeValueSlideIndex];
    if (selectedSlideLeft === undefined) {
      swiperInstance.slideTo(selectedAttributeValueSlideIndex, 0);
    } else {
      swiperInstance.translateTo(-selectedSlideLeft + SELECTED_TILE_LEFT_OFFSET, 0);
    }
    syncNavigationArrowsFromSwiper();
  }, [swiperInstance, selectedAttributeValueSlideIndex, syncNavigationArrowsFromSwiper]);

  useEffect(() => {
    if (swiperInstance === undefined || swiperInstance.destroyed) {
      return;
    }
    swiperInstance.update();
    positionSelectedSlide();
  }, [swiperInstance, positionSelectedSlide]);

  useEffect(() => {
    if (swiperInstance === undefined || swiperInstance.destroyed) {
      return;
    }
    const syncOnLayout = (): void => {
      syncNavigationArrowsFromSwiper();
    };
    // A resize re-snaps the selected tile flush-left (back under the arrow), so re-apply the
    // offset; other events only need the arrow-visibility sync.
    swiperInstance.on("resize", positionSelectedSlide);
    swiperInstance.on("slidesUpdated", syncOnLayout);
    swiperInstance.on("slideChange", syncOnLayout);
    swiperInstance.on("setTranslate", syncOnLayout);
    swiperInstance.on("transitionEnd", syncOnLayout);
    swiperInstance.on("reachBeginning", syncOnLayout);
    swiperInstance.on("reachEnd", syncOnLayout);
    swiperInstance.on("fromEdge", syncOnLayout);
    swiperInstance.on("toEdge", syncOnLayout);
    return (): void => {
      if (swiperInstance.destroyed) {
        return;
      }
      swiperInstance.off("resize", positionSelectedSlide);
      swiperInstance.off("slidesUpdated", syncOnLayout);
      swiperInstance.off("slideChange", syncOnLayout);
      swiperInstance.off("setTranslate", syncOnLayout);
      swiperInstance.off("transitionEnd", syncOnLayout);
      swiperInstance.off("reachBeginning", syncOnLayout);
      swiperInstance.off("reachEnd", syncOnLayout);
      swiperInstance.off("fromEdge", syncOnLayout);
      swiperInstance.off("toEdge", syncOnLayout);
    };
  }, [swiperInstance, syncNavigationArrowsFromSwiper, positionSelectedSlide]);

  const getAttributeValueClassName = (productAttributeOption: ProductAttributeOption): string => {
    if (productAttributeOption.attributeValueId === productAttributeState.selectedAttributeValueId) {
      return "tileSelected";
    } else {
      if (productAttributeOption.isValidCombination === true) {
        return "tileNotSelected";
      } else {
        return "tileNotAvailable";
      }
    }
  };

  const renderTile = (productAttributeOption: ProductAttributeOption): ReactNode => {
    const isCurrentPage = productAttributeOption.attributeValueId === productAttributeState.selectedAttributeValueId;
    const chipClassName = `${style.tile} ${style[getAttributeValueClassName(productAttributeOption)]} ${
      variantNavigationLocked ? style.navigationLocked : ""
    }`;

    const useLink =
      !isCurrentPage &&
      !variantNavigationLocked &&
      productAttributeOption.url.length > 0 &&
      productAttributeOption.mpId > 0;

    return useLink ? (
      <Link
        key={productAttributeOption.attributeValueId}
        href={productAttributeOption.url}
        className={chipClassName}
        scroll={false}
        onClick={(e) => {
          // Do not show the loading overlay when the user opens this link in a new tab/window
          // (e.g. Cmd/Ctrl/middle-click) - the current page is not navigating.
          if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
            return;
          }
          if (e.button !== 0) {
            return;
          }
          onVariantNavigationStart();
        }}
      >
        {productAttributeOption.attributeValue}
      </Link>
    ) : (
      <span key={productAttributeOption.attributeValueId} className={chipClassName}>
        {productAttributeOption.attributeValue}
      </span>
    );
  };

  // The "Show All / Show Less" toggle replaces the swiper with a wrapped, fully-expanded list.
  // It only appears when the collapsed row overflows (once expanded it stays, to allow collapse).
  const showToggle = hasOverflow || expanded;
  const toggle = showToggle ? (
    <button
      type="button"
      className={style.showAllToggle}
      onClick={() => dispatch(setVariantAttributeExpanded({ attributeName: atributeName, expanded: !expanded }))}
      aria-expanded={expanded}
    >
      {expanded ? "Show Less" : "Show All"}
      <CaretDownIcon
        className={`${style.caret} ${expanded ? style.caretExpanded : ""}`}
        width={12}
        height={12}
        aria-hidden
      />
    </button>
  ) : null;

  return (
    <ProductVariantAttribute atributeName={atributeName} atributeValue={atributeValue} action={toggle}>
      {expanded ? (
        <div className={style.wrappedTiles}>
          {productAttributeState.productAttributeOptions.map((productAttributeOption) =>
            renderTile(productAttributeOption),
          )}
        </div>
      ) : (
        <div className={style.ProductVariantAttributeValuesSwiper}>
          <div
            aria-hidden={!showPrev}
            className={`${style.swiperArrow} ${style.swiperArrowPrev} ${showPrev ? "" : style.swiperArrowHidden}`}
          >
            <span className={style.scrollButtonBacking}>
              <button
                type="button"
                className={style.scrollButton}
                aria-label="Previous options"
                onClick={() => scrollBySlide(-1)}
              >
                <ChevronLeftIcon className={style.chevron} width={16} height={16} aria-hidden />
              </button>
            </span>
            <span className={`${style.gradientFade} ${style.gradientFadeLeft}`} aria-hidden />
          </div>

          <div className={style.swiperWrapper}>
            <Swiper
              onSwiper={(swiper) => setSwiperInstance(swiper)}
              onDestroy={() => setSwiperInstance(undefined)}
              initialSlide={selectedAttributeValueSlideIndex}
              slidesPerView={"auto"}
              loop={false}
              direction="horizontal"
              autoHeight={true}
              spaceBetween={8}
            >
              {productAttributeState.productAttributeOptions.map((productAttributeOption) => (
                <SwiperSlide key={productAttributeOption.attributeValueId}>
                  {renderTile(productAttributeOption)}
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <div
            aria-hidden={!showNext}
            className={`${style.swiperArrow} ${style.swiperArrowNext} ${showNext ? "" : style.swiperArrowHidden}`}
          >
            <span className={`${style.gradientFade} ${style.gradientFadeRight}`} aria-hidden />
            <span className={style.scrollButtonBacking}>
              <button
                type="button"
                className={style.scrollButton}
                aria-label="More options"
                onClick={() => scrollBySlide(1)}
              >
                <ChevronRightIcon className={style.chevron} width={16} height={16} aria-hidden />
              </button>
            </span>
          </div>
        </div>
      )}
    </ProductVariantAttribute>
  );
}

type ProductVariantAttributeValuesSwiperProps = {
  atributeName: string;
  atributeValue: string;
  productAttributeState: ProductAttributeState;
  onVariantNavigationStart: () => void;
  variantNavigationLocked?: boolean;
};

export default ProductVariantAttributeValuesSwiper;
